import React, { useState } from 'react';
import './style.less';

const WORKSPACE_TABS = [
  { id: 'ide', label: 'Web IDE 编码' },
  { id: 'ai', label: 'AI 智能体协同' },
  { id: 'dlp', label: 'DLP 与 SSH 管控' },
];

const HERO_METRICS = [
  {
    title: '秒级就绪',
    badge: 'DaemonSet 预热',
    desc: '工作间镜像预热至集群节点，创建即开，免除现场拉取数 GB 镜像',
  },
  {
    title: '双模 IDE + 双智能体',
    badge: 'Codex & Steerable',
    desc: 'Web IDE / JupyterLab / 本地 IDE 全支持，主容器内置双编程智能体',
  },
  {
    title: '五层 DLP 防泄漏',
    badge: '禁止优先',
    desc: '平台/项目/角色/个人/例外五层策略合并，撤权后 60 秒内阻断活跃会话',
  },
  {
    title: '180 天哈希链审计',
    badge: 'p95 ≤ 2s 检索',
    desc: '覆盖登录、工作间、上传下载、SFTP 与端口转发，SHA-256 防篡改留存',
  },
];

const HeroSection = () => {
  const [activePreview, setActivePreview] = useState('ide');

  const scrollToTarget = (selector) => {
    const el = document.querySelector(selector);
    if (el) {
      const top = el.getBoundingClientRect().top + window.scrollY - 76;
      window.scrollTo({ top, behavior: 'smooth' });
    }
  };

  return (
    <div className="hero-wrapper">
      <div className="hero-content">
        <div className="hero-text">
          <div className="hero-badge">
            <span className="badge-dot" aria-hidden="true" />
            <span>企业级云端研发与外包安全协作平台 · 支持专有云离线交付</span>
          </div>

          <h1>
            <span className="title-main">安全可控的企业级云端研发平台</span>
            <span className="title-accent">一键启动云端工作间，让研发提效与源码安全兼得</span>
          </h1>

          <p className="hero-subtitle">
            基于容器化工作间与统一镜像底座，原生支持 VS Code、JupyterLab 与 JetBrains / Remote-SSH 双模开发；主容器预装{' '}
            <strong>Codex</strong> 与 <strong>Steerable</strong> 双 AI 智能体，内置项目级外包限期授权、五层数据防泄漏（DLP）与等保三级合规审计。
          </p>

          <div className="hero-actions">
            <a
              className="primary-cta"
              href="https://console.geekcode.cloud/login/"
              target="_blank"
              rel="noopener noreferrer"
            >
              <span>立即登录</span>
              <span aria-hidden="true">↗</span>
            </a>
            <button
              type="button"
              className="secondary-cta"
              onClick={() => scrollToTarget('#features')}
            >
              <span>了解核心能力</span>
              <span aria-hidden="true">↓</span>
            </button>
            <button
              type="button"
              className="tertiary-cta"
              onClick={() => scrollToTarget('#video-demo')}
            >
              <span className="play-icon" aria-hidden="true">▶</span>
              <span>观看 1 分钟演示</span>
            </button>
          </div>

          <div className="hero-trust-pills" aria-label="关键特性摘要">
            <span>✓ 镜像节点预热秒开</span>
            <span>✓ 上游 API 密钥不出控制面</span>
            <span>✓ 五层 DLP 禁止优先</span>
            <span>✓ 签名离线包一键交付</span>
          </div>
        </div>

        <div className="hero-stage">
          <div className="workspace-window cloud-collab-card">
            <div className="window-titlebar">
              <div className="window-dots" aria-hidden="true">
                <span className="dot red" />
                <span className="dot amber" />
                <span className="dot green" />
              </div>
              <div className="window-meta">
                <span className="ws-name">ws-core-payment</span>
                <span className="ws-spec">Java 17 · 2C4G20G</span>
              </div>
              <div className="window-status">
                <span className="status-dot" />
                <span>运行中</span>
              </div>
            </div>

            <div className="window-toolbar" role="tablist" aria-label="工作间预览视图">
              {WORKSPACE_TABS.map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  role="tab"
                  aria-selected={activePreview === tab.id}
                  className={`toolbar-tab ${activePreview === tab.id ? 'active' : ''}`}
                  onClick={() => setActivePreview(tab.id)}
                >
                  {tab.label}
                </button>
              ))}
              <span className="toolbar-project">项目：核心交易平台</span>
            </div>

            <div className="window-body">
              <div className="watermark-overlay" aria-hidden="true">
                <span>dev_zhangsan · 10.20.3.18 · 2026-10-20</span>
                <span>dev_zhangsan · 10.20.3.18 · 2026-10-20</span>
              </div>

              {activePreview === 'ide' && (
                <div className="preview-pane ide-pane">
                  <div className="ide-layout">
                    <div className="ide-sidebar">
                      <div className="tree-header">资源管理器</div>
                      <div className="tree-item folder">▾ /home/workspace</div>
                      <div className="tree-item indent">▾ src/main/java</div>
                      <div className="tree-item file active">PaymentService.java</div>
                      <div className="tree-item file">AuditInterceptor.java</div>
                      <div className="tree-item file">pom.xml</div>
                      <div className="volume-badge">
                        <span className="vol-tag">独立存储卷</span>
                        <code>vol-payment-01 (20Gi)</code>
                      </div>
                    </div>
                    <div className="ide-editor">
                      <div className="editor-header">
                        <span className="file-tab">PaymentService.java</span>
                        <span className="port-pill">同源端口预览 :8080 Ready</span>
                      </div>
                      <pre className="code-block">
                        <code>
{`14 | @Service
15 | public class PaymentService {
16 |   // 工作间同源端口路径直接预览：
17 |   // /ideVs/projects/core/workspaces/ws-01/ports/8080/
18 |   @Transactional(rollbackFor = Exception.class)
19 |   public SettlementReceipt settle(Order order) {
20 |     AuditChain.record("payment.settle", order.getId());
21 |     return gateway.commit(order);
22 |   }
23 | }`}
                        </code>
                      </pre>
                      <div className="mini-terminal">
                        <div className="term-header">
                          <span>终端 · workspace@ws-core-payment:/home/workspace</span>
                          <span className="term-badge">UID 1000</span>
                        </div>
                        <div className="term-line">
                          <span className="prompt">$</span> codex &quot;校验结算幂等逻辑并补充单元测试&quot;
                        </div>
                        <div className="term-output">
                          ✓ 已通过平台模型网关 (/api/v1/ai/v1 · gct_***) 响应并执行 mvn test (12 passed)
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {activePreview === 'ai' && (
                <div className="preview-pane ai-pane">
                  <div className="pane-top-row">
                    <span className="pane-badge">主容器原生智能体 · 共享 JDK / Maven / GCC 工具链</span>
                    <span className="pane-status">Secret 卷热更新 ≤1min</span>
                  </div>
                  <div className="terminal-card">
                    <div className="term-line">
                      <span className="prompt">$</span> geekcode-ai status
                    </div>
                    <div className="term-grid">
                      <div><span>所属项目：</span><strong>核心交易平台 (已启用智能体)</strong></div>
                      <div><span>模型服务：</span><strong>企业内网网关 (/api/v1/ai/v1)</strong></div>
                      <div><span>Codex 默认模型：</span><strong>DeepSeek-V3-Enterprise (智能体可用)</strong></div>
                      <div><span>Steerable 默认模型：</span><strong>Qwen-2.5-Coder-32B (流式 + 工具调用)</strong></div>
                    </div>
                    <div className="term-line">
                      <span className="prompt">$</span> steerable &quot;分析当前模块依赖并生成接口契约测试&quot;
                    </div>
                    <div className="term-output">
                      ▸ 读取 /etc/geekcode/ai/agent.json · 携带工作间专属令牌 Authorization: Bearer gct_9f2a...
                      <br />
                      ▸ 正在调用主容器内 Maven 3.9 执行验证 · 上游 API Key 全程保留在控制面 Secret
                    </div>
                  </div>
                </div>
              )}

              {activePreview === 'dlp' && (
                <div className="preview-pane dlp-pane">
                  <div className="pane-top-row">
                    <span className="pane-badge">五层 DLP 策略合成（平台 ∩ 项目 ∩ 角色 ∩ 个人 ∩ 例外）</span>
                    <span className="pane-status warn">禁止优先原则生效</span>
                  </div>
                  <div className="dlp-matrix">
                    <div className="dlp-item">
                      <span className="dlp-label">Web IDE 文件下载</span>
                      <span className="dlp-val blocked">已禁止 (隐藏下载入口)</span>
                    </div>
                    <div className="dlp-item">
                      <span className="dlp-label">编辑器/终端剪贴板复制</span>
                      <span className="dlp-val blocked">已拦截 (Copy/Cut 阻断)</span>
                    </div>
                    <div className="dlp-item">
                      <span className="dlp-label">SSH / 本地 IDE 远程接入</span>
                      <span className="dlp-val blocked">按项目禁用 (60s 内强制断连)</span>
                    </div>
                    <div className="dlp-item">
                      <span className="dlp-label">单向文件拖拽上传</span>
                      <span className="dlp-val allowed">允许写入 /home/workspace (含审计)</span>
                    </div>
                  </div>
                  <div className="dlp-audit-note">
                    <span>实时防篡改水印：</span>
                    <code>登录名 + 真实来源 IP + 时间戳（DOM 删除或隐藏后自动恢复）</code>
                  </div>
                </div>
              )}
            </div>

            <div className="window-footer">
              <span className="footer-shield">🛡</span>
              {activePreview === 'ide' && (
                <span>同源端口转发已就绪（无需泛域名与额外证书） · 独立存储卷 vol-payment-01 已挂载</span>
              )}
              {activePreview === 'ai' && (
                <span>模型网关已连接（Responses / Chat Completions 透明适配） · 密钥不出控制面</span>
              )}
              {activePreview === 'dlp' && (
                <span>五层 DLP 防泄漏策略强制生效中 · 水印防删改守护正常 · SHA-256 哈希链审计已开启</span>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="hero-metrics-bar">
        {HERO_METRICS.map((item) => (
          <div key={item.title} className="metric-card cloud-collab-card">
            <div className="metric-top">
              <span className="metric-title">{item.title}</span>
              <span className="metric-badge">{item.badge}</span>
            </div>
            <p className="metric-desc">{item.desc}</p>
          </div>
        ))}
      </div>
    </div>
  );
};

export default HeroSection;
